import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, Grid, IconButton, Paper } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { NoteDTO, UnmarkedNoteDTO, getUserByEmail, updateUnmarkedNotes } from 'wisestep-api';
import { Label, WsButton, WsColors, WsTypoVariants, WsTypography } from 'wisestep-base';

import ClientAutoCompleteFilter from '../../../components/clientAutoCompleteFilter/ClientAutoCompleteFilter';
import ClientContactAutoComplete from '../../../components/clientContactAutoComplete/ClientContactAutoComplete';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';

interface IUnmarkedMissingDetailsProps {
  onClose: () => void;
  noteDTO: NoteDTO;
}

const UnmarkedMissingDetails = ({ onClose, noteDTO }: IUnmarkedMissingDetailsProps) => {
  const methods = useForm<UnmarkedNoteDTO>();

  const { handleSubmit, watch, getValues } = methods;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const companyName = watch('companyName') ?? '';
  const enteredRecruiter = watch('clientRecruiter') ?? '';
  const data = enteredRecruiter.includes('|') ? enteredRecruiter.split('|')[0] : enteredRecruiter;
  const clientRecruiter = data?.split(' - ')[1] ?? data;

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const { clientId, clientContactId, createdBy } = data;

      const hasNoteClient = !!noteDTO?.client?.id;
      const hasNoteContact = !!noteDTO?.clientContact?.id;
      const hasNoteCreatedBy = !!noteDTO?.createdBy;

      const recruiterEmail = createdBy?.[0];
      const recruiter =
        !hasNoteCreatedBy && recruiterEmail ? (await getUserByEmail(recruiterEmail)).data : null;

      const payload = {
        noteId: noteDTO.id,
        clientId: hasNoteClient ? noteDTO.client.id : clientId ?? '',
        clientContactId: hasNoteContact ? noteDTO.clientContact.id : clientContactId ?? '',
        createdBy: hasNoteCreatedBy ? noteDTO.createdBy : recruiterEmail ?? '',
        createdByName: hasNoteCreatedBy
          ? noteDTO.createdByName
          : recruiter
          ? `${recruiter.firstName} ${recruiter.lastName}`
          : undefined,
      };

      await updateUnmarkedNotes(payload);
      onClose();
    } catch (error) {
      console.error('Error saving missing details:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item>
            <WsTypography bold variant={WsTypoVariants.SUB_TITLE}>
              Add Missing Details
            </WsTypography>
          </Grid>
          <Grid item>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ fill: WsColors.GreyBlack, cursor: 'pointer' }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }} role="presentation">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent>
              <Grid container spacing={2}>
                {!noteDTO.client && (
                  <Grid item xs={6}>
                    <ClientAutoCompleteFilter
                      name="companyName"
                      label="Client"
                      clientIdFieldName="clientId"
                      showMandatorySymbol={true}
                    />
                  </Grid>
                )}
                {!noteDTO.clientContact && (
                  <Grid item xs={6}>
                    <ClientContactAutoComplete
                      key={clientRecruiter}
                      name="clientRecruiter"
                      clientId={getValues('clientId') ?? noteDTO?.client?.id ?? ''}
                      showMandatorySymbol={true}
                      disabled={isEmpty(companyName?.trim())}
                      clientName={companyName}
                      clientContactIdFieldName="clientContactId"
                      component="job"
                    />
                  </Grid>
                )}
                {!noteDTO.createdBy && (
                  <Grid item xs={6}>
                    <Label>Created By *</Label>
                    <RecruiterSuggestions name="createdBy" required />
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid container spacing={2} justifyContent="end">
                <Grid item>
                  <WsButton onClick={onClose} size="large">
                    Cancel
                  </WsButton>
                </Grid>
                <Grid item>
                  <WsButton type="submit" size="large" disabled={isSubmitting}>
                    Save
                  </WsButton>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        </FormProvider>
      </Paper>
    </>
  );
};

export default UnmarkedMissingDetails;
